import { useEffect, useMemo, useState } from 'react';
import { Calendar, Clock } from 'lucide-react';
import {
  MEASUREMENT_INTERVALS,
  formatMeasurementDayLabel,
  humanizeMeasurementSlot,
  intervalButtonLabel,
  listMeasurementDays,
  parseIsoDateLocal,
  type MeasurementIntervalCode,
} from '../lib/measurementSlots';

type Props = {
  value: string;
  onChange: (preferredTime: string) => void;
  disabled?: boolean;
  onHaptic?: () => void;
};

type Step = 'day' | 'interval' | 'custom';

export function MeasurementSlotPicker({ value, onChange, disabled, onHaptic }: Props) {
  const days = useMemo(() => listMeasurementDays(10), []);
  const [step, setStep] = useState<Step>('day');
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(Boolean(value.trim()));

  useEffect(() => {
    if (!value.trim()) {
      setCollapsed(false);
      setStep('day');
      setSelectedDay(null);
    }
  }, [value]);

  const tap = () => onHaptic?.();

  const pickDay = (iso: string) => {
    tap();
    setSelectedDay(iso);
    setStep('interval');
    setCollapsed(false);
  };

  const pickInterval = (code: MeasurementIntervalCode) => {
    if (!selectedDay) return;
    tap();
    onChange(humanizeMeasurementSlot(selectedDay, code));
    setCollapsed(true);
    setStep('day');
    setSelectedDay(null);
  };

  const chipStyle = (active: boolean) =>
    active
      ? { background: 'rgba(99,179,237,0.2)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }
      : { background: 'rgba(99,179,237,0.1)', color: 'var(--cyan)', border: '1px solid var(--border-glass)' };

  if (collapsed && value.trim()) {
    return (
      <div className="space-y-2">
        <div
          className="rounded-xl px-3 py-2.5 flex items-start gap-2"
          style={{ background: 'rgba(99,179,237,0.08)', border: '1px solid var(--cyan-border)' }}
        >
          <Clock size={16} className="shrink-0 mt-0.5" style={{ color: 'var(--cyan-bright)' }} />
          <p className="text-xs font-semibold leading-snug flex-1" style={{ color: 'var(--text-1)' }}>
            {value}
          </p>
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            tap();
            setCollapsed(false);
            setStep('day');
            setSelectedDay(null);
          }}
          className="press text-[11px] font-bold underline touch-manipulation min-h-[40px]"
          style={{ color: 'var(--cyan)' }}
        >
          Изменить дату и интервал
        </button>
      </div>
    );
  }

  if (step === 'custom') {
    return (
      <div className="space-y-2">
        <textarea
          className="input-glass w-full text-sm min-h-[72px] rounded-xl p-3 resize-none touch-manipulation"
          placeholder="Например: 28.05.2026 с 10:00 до 14:00"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            tap();
            setStep('day');
            setSelectedDay(null);
          }}
          className="press text-[11px] font-bold touch-manipulation min-h-[40px]"
          style={{ color: 'var(--cyan)' }}
        >
          ← Выбрать день и интервал из списка
        </button>
      </div>
    );
  }

  if (step === 'interval' && selectedDay) {
    const dayLabel = formatMeasurementDayLabel(parseIsoDateLocal(selectedDay));
    return (
      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-wide flex items-center gap-1" style={{ color: 'var(--cyan)' }}>
          <Calendar size={12} /> {dayLabel} — выберите интервал
        </p>
        <div className="flex flex-wrap gap-2">
          {MEASUREMENT_INTERVALS.map((slot) => (
            <button
              key={slot.code}
              type="button"
              disabled={disabled}
              onClick={() => pickInterval(slot.code)}
              className="press px-3 py-2 rounded-xl text-[11px] font-bold touch-manipulation min-h-[44px]"
              style={chipStyle(false)}
            >
              🕐 {intervalButtonLabel(slot.code)}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              tap();
              setSelectedDay(null);
              setStep('day');
            }}
            className="press text-[11px] font-bold touch-manipulation min-h-[40px]"
            style={{ color: 'var(--text-3)' }}
          >
            ← Другой день
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              tap();
              setStep('custom');
            }}
            className="press text-[11px] font-bold touch-manipulation min-h-[40px]"
            style={{ color: 'var(--cyan)' }}
          >
            Свой интервал текстом
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-3)' }}>
        Удобный день выезда
      </p>
      <div className="flex flex-wrap gap-2">
        {days.map((d) => (
          <button
            key={d.iso}
            type="button"
            disabled={disabled}
            onClick={() => pickDay(d.iso)}
            className="press px-2.5 py-2 rounded-xl text-[11px] font-semibold touch-manipulation min-h-[44px]"
            style={chipStyle(false)}
          >
            📅 {d.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          tap();
          setStep('custom');
        }}
        className="press text-[11px] font-bold touch-manipulation min-h-[40px]"
        style={{ color: 'var(--text-3)' }}
      >
        ✏️ Свой интервал текстом
      </button>
    </div>
  );
}
